import React, { useLayoutEffect, useState } from 'react';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import PropTypes from 'prop-types';
import { change, clearFields } from 'redux-form';
import Content from '../../../layout/Content';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import BaggageManifestForm, {
  BAGGAGE_MANIFEST_FORM,
} from './BaggageManifestForm';
import {
  BAGGAGE_MANIFEST_ACTIONS,
  REPORT_ISSUE_OPTIONS,
} from '../../../../config/constants';
import {
  clearBaggageItems,
  getItemsByItineraryId,
  postMarkNotArrived,
  postReportIssue,
  putChangeItemItinerary,
  putDeliverItems,
  putDismissItemItinerary,
  putForceDeliverItems,
  putMarkSeized,
} from '../../../../actions';
import { clearBaggageManifestPrint } from '../../../../actions/baggage/BaggageManifestPrint';
import Modal from '../../../common/modal/Modal';
import BaggageManifestDestinationMismatchModal from './baggage-manifest/BaggageManifestDestinationMismatchModal';

const BaggageManifest = ({
  breadcrumbs,
  dispatchPostReportIssue,
  dispatchPutDismissItemItinerary,
  dispatchGetItemsByItineraryId,
  dispatchClearFields,
  dispatchPutChangeItemItinerary,
  dispatchPutDeliverItems,
  dispatchClearBaggageItems,
  dispatchClearBaggageManifestPrint,
  dispatchPostMarkNotArrived,
  dispatchPutForceDeliverItems,
  dispatchPutMarkSeized,
}) => {
  useLayoutEffect(
    () => () => {
      dispatchClearBaggageItems();
      dispatchClearBaggageManifestPrint();
    },
    [],
  );

  const [titleModalConfirmation, setTitleModalConfirmation] = useState(null);
  const [contentModalConfirmation, setContentModalConfirmation] =
    useState(null);
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);

  const resetForm = () => {
    dispatchClearFields(BAGGAGE_MANIFEST_FORM, false, false, 'comment');
  };

  const cancelConfirmation = async ({ itineraryId }) => {
    setShowModalConfirmation(false);
    resetForm();
    await dispatchGetItemsByItineraryId({ itineraryId });
  };

  const handleForceDeliver = async (formValues) => {
    setShowModalConfirmation(false);
    const { items, itineraryId, comment, locationId } = formValues;
    await dispatchPutForceDeliverItems({
      itemIds: items || [],
      comment,
      locationId,
    });
    await dispatchGetItemsByItineraryId({ itineraryId });
  };

  const handleSubmit = async (formValues) => {
    const { action, location, items, itineraryId, comment } = formValues;

    if (!itineraryId) {
      toastr.error('Error', 'Debe seleccionar un itinerario para continuar');
      return;
    }

    if (!items || items.length === 0) {
      toastr.error(
        'Error',
        'Debe seleccionar al menos un equipaje para continuar',
      );
      return;
    }

    if (!action) {
      toastr.error('Error', 'Debe seleccionar una acción para continuar');
      return;
    }

    const { value: actionValue } = action;

    if (actionValue === BAGGAGE_MANIFEST_ACTIONS.DISMISS.value) {
      if (!location) {
        toastr.error('Error', 'Debe seleccionar una ubicación para continuar');
        return;
      }

      const { value: locationId } = location;

      await dispatchPutDismissItemItinerary({
        itemIds: items,
        locationId,
        comment,
      });
    }

    if (actionValue === BAGGAGE_MANIFEST_ACTIONS.CHANGE_ITINERARY.value) {
      const { newItineraryId } = formValues;

      if (!newItineraryId) {
        toastr.error(
          'Error',
          'Debe seleccionar un nuevo itinerario para continuar',
        );
        return;
      }

      await dispatchPutChangeItemItinerary({
        itemIds: items,
        newItineraryId,
        comment,
      });
    }

    if (actionValue === BAGGAGE_MANIFEST_ACTIONS.DELIVER.value) {
      if (!location) {
        toastr.error('Error', 'Debe seleccionar una ubicación para continuar');
        return;
      }

      const { value: locationId } = location;

      const result = await dispatchPutDeliverItems({
        itemIds: items,
        locationId,
        comment,
      });

      const itemsWithDestinationMismatch = result.filter(
        (r) => r.destinationMismatch,
      );

      if (itemsWithDestinationMismatch.length > 0) {
        const { itemObjects } = formValues;

        const mismatchedItemsDetails = itemObjects.filter((item) =>
          itemsWithDestinationMismatch.some(
            (mismatched) => mismatched.itemId === item.id,
          ),
        );

        const content = (
          <BaggageManifestDestinationMismatchModal
            items={mismatchedItemsDetails}
            onCancel={() => cancelConfirmation({ itineraryId })}
            onSubmit={handleForceDeliver}
            itineraryId={itineraryId}
            locationId={locationId}
          />
        );
        setTitleModalConfirmation(
          'Equipajes con destino diferente a la Ubicación de Entrega',
        );
        setContentModalConfirmation(content);
        setShowModalConfirmation(true);
        return;
      }
    }

    if (actionValue === BAGGAGE_MANIFEST_ACTIONS.REPORT_ISSUE.value) {
      const { reportIssueType } = formValues;

      if (!reportIssueType) {
        toastr.error(
          'Error',
          'Debe seleccionar un tipo de problema para continuar',
        );
        return;
      }

      if (reportIssueType.isIncident) {
        await dispatchPostReportIssue({
          itemIds: items,
          comment,
          locationId: location ? location.value : null,
          itineraryId,
          itemIncidentType: reportIssueType.value,
        });
      }
      if (
        reportIssueType.value === REPORT_ISSUE_OPTIONS.NOT_ARRIVED.value &&
        !reportIssueType.isIncident
      ) {
        await dispatchPostMarkNotArrived({
          itemIds: items,
          comment,
          locationId: location ? location.value : null,
          itineraryId,
        });
      }

      if (
        reportIssueType.value === REPORT_ISSUE_OPTIONS.SEIZED.value &&
        !reportIssueType.isIncident
      ) {
        await dispatchPutMarkSeized({
          itemIds: items,
          comment,
          locationId: location ? location.value : null,
          itineraryId,
        });
      }
    }

    resetForm();

    await dispatchGetItemsByItineraryId({ itineraryId });
  };

  const content = (
    <>
      <BaggageManifestForm onSubmit={handleSubmit} />
    </>
  );

  return (
    <>
      <Modal
        title={titleModalConfirmation}
        body={contentModalConfirmation}
        show={showModalConfirmation}
      />
      <Content
        title="Manifiesto de Equipaje"
        subtitle="Búsqueda de paqueres por itinerario"
        content={content}
        breadcrumbs={breadcrumbs}
      />
    </>
  );
};

BaggageManifest.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchPutDismissItemItinerary: PropTypes.func.isRequired,
  dispatchGetItemsByItineraryId: PropTypes.func.isRequired,
  dispatchClearFields: PropTypes.func.isRequired,
  dispatchPutChangeItemItinerary: PropTypes.func.isRequired,
  dispatchPutDeliverItems: PropTypes.func.isRequired,
  dispatchClearBaggageItems: PropTypes.func.isRequired,
  dispatchPostReportIssue: PropTypes.func.isRequired,
  dispatchClearBaggageManifestPrint: PropTypes.func.isRequired,
  dispatchPostMarkNotArrived: PropTypes.func.isRequired,
  dispatchPutForceDeliverItems: PropTypes.func.isRequired,
  dispatchPutMarkSeized: PropTypes.func.isRequired,
};

BaggageManifest.defaultProps = {};

const mapStateToProps = ({ BaggageUnit }) => ({
  breadcrumbs: [
    ...BaggageUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Manifiesto de Equipaje',
      href: '',
    },
  ],
});

const mapDispatchToProps = {
  dispatchPutDismissItemItinerary: putDismissItemItinerary,
  dispatchGetItemsByItineraryId: getItemsByItineraryId,
  dispatchChange: change,
  dispatchClearFields: clearFields,
  dispatchPutChangeItemItinerary: putChangeItemItinerary,
  dispatchPutDeliverItems: putDeliverItems,
  dispatchClearBaggageItems: clearBaggageItems,
  dispatchPostReportIssue: postReportIssue,
  dispatchClearBaggageManifestPrint: clearBaggageManifestPrint,
  dispatchPostMarkNotArrived: postMarkNotArrived,
  dispatchPutForceDeliverItems: putForceDeliverItems,
  dispatchPutMarkSeized: putMarkSeized,
};

export default connect(mapStateToProps, mapDispatchToProps)(BaggageManifest);
